import React, { useEffect, useState } from "react";
import ApiEnterpriseName from "./api_hits/ApiEnterpriseName";
import GenericTable from "../GenericTable/table";

const API_URL = process.env.REACT_APP_API_URL;

const API_BASE = API_URL;
const TOKEN = "<YOUR_BEARER_TOKEN>";

const ALLOWED_EXTENSIONS = ["csv", "geojson", "json", "kml"];

/* ---------------------------------------------------- */
/* Types */
/* ---------------------------------------------------- */

interface UploadHistory {
  enterprise: string;
  uploaded_by: string;
  datetime: string;
  status: string;
  summary: string;
  success_count: number;
  failure_count: number;
  error_preview?: {
    farm_name: string;
    error: string;
  }[];
  download_url?: {
    success?: string;
    failed?: string;
  };
}

export default function UserFarmBulkUpload() {

  const [selectedEnterprise, setSelectedEnterprise] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadHistory, setUploadHistory] = useState<UploadHistory[]>([]);

  /* ---------------------------------------------------- */
  /* FILE SELECT */
  /* ---------------------------------------------------- */

  const handleFileSelect = (selectedFile: File) => {

    const ext = selectedFile.name.split(".").pop()?.toLowerCase();

    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      alert("Upload CSV / GeoJSON / JSON / KML file");
      return;
    }

    setFile(selectedFile);
    uploadFile(selectedFile);
  };

  /* ---------------------------------------------------- */
  /* UPLOAD FILE */
  /* ---------------------------------------------------- */

  const uploadFile = async (selectedFile: File) => {

    if (!selectedEnterprise) {
      alert("Please select enterprise");
      return;
    }

    const formData = new FormData();

    formData.append("file", selectedFile);
    formData.append("enterprise_id", String(selectedEnterprise));

    setLoading(true);

    try {

      await fetch(`${API_BASE}/onboarding/s3/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${TOKEN}`,
        },
        body: formData,
      });

      fetchHistory();

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);
      setFile(null);

    }
  };

  /* ---------------------------------------------------- */
  /* FETCH HISTORY */
  /* ---------------------------------------------------- */

  const fetchHistory = async () => {

    try {

      const res = await fetch(`${API_BASE}/onboarding/s3/history`, {
        headers: {
          Authorization: `Bearer ${TOKEN}`,
        },
      });

      const data: UploadHistory[] = await res.json();

      setUploadHistory(data || []);

    } catch (err) {

      console.error(err);

    }
  };

  /* ---------------------------------------------------- */
  /* INITIAL LOAD */
  /* ---------------------------------------------------- */

  useEffect(() => {

    fetchHistory();

  }, []);

  /* ---------------------------------------------------- */
  /* POLLING WHILE PROCESSING */
  /* ---------------------------------------------------- */

  useEffect(() => {

    const hasProcessing = uploadHistory.some(
      (row) => row.status === "Processing"
    );

    if (!hasProcessing) return;

    const interval = setInterval(() => {

      fetchHistory();

    }, 5000);

    return () => clearInterval(interval);

  }, [uploadHistory]);

  /* ---------------------------------------------------- */
  /* RENDER */
  /* ---------------------------------------------------- */

  return (
    <div className="container-fluid py-4">

      <h3 className="fw-bold">Bulk Upload</h3>

      {/* Upload Section */}

      <div className="card mb-4">

        <div className="card-body">

          <label className="fw-semibold mb-2">Select Enterprise</label>

          <div className="d-flex gap-3">

            <ApiEnterpriseName
              selectedEnterprise={selectedEnterprise}
              setSelectedEnterprise={setSelectedEnterprise}
            />

            <button
              className="btn btn-success"
              disabled={!selectedEnterprise || loading}
              onClick={() =>
                (document.getElementById("fileInput") as HTMLInputElement)?.click()
              }
            >
              {loading ? "Uploading..." : "Upload File"}
            </button>

          </div>

          <input
            id="fileInput"
            type="file"
            accept=".csv,.geojson,.json,.kml"
            className="d-none"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {

              const selected = e.target.files?.[0];

              if (!selected) return;

              handleFileSelect(selected);

            }}
          />

        </div>

      </div>

      {/* History Table */}

      <div className="card">

        <div className="card-header fw-bold">

          Upload History

        </div>

        <div className="card-body p-0">

          <GenericTable
            columns={[
              "Enterprise",
              "Uploaded By",
              "Date",
              "Status",
              "Summary",
              "Success",
              "Failure",
              "Action"
            ]}
            data={uploadHistory}
            pageSize={5}
            searchTerm=""
            handleSort={() => {}}
            sortConfig={{ key: "", direction: null }}
            onSearch={() => {}}
            renderCell={(col: string, row: Record<string, any>, rowIndex: number) => {

  const data = row as UploadHistory;

  switch (col) {

    case "Enterprise":
      return data.enterprise;

    case "Uploaded By":
      return data.uploaded_by;

    case "Date":
      return new Date(data.datetime).toLocaleString();

    case "Status":
      return (
        <span
          className={`badge ${
            data.status === "Completed"
              ? "bg-success"
              : data.status === "Failed"
              ? "bg-danger"
              : data.status === "CompletedWithErrors"
              ? "bg-warning text-dark"
              : "bg-secondary"
          }`}
        >
          {data.status}
        </span>
      );

    case "Summary":
      return data.summary;

    case "Success":
      return (
        <span className="text-success fw-bold">
          {data.success_count}
        </span>
      );

    case "Failure":
      return (
        <span className="text-danger fw-bold">
          {data.failure_count}
        </span>
      );

    case "Action":

      if (!data.download_url) return "-";

      return (
        <div className="d-flex gap-2">

          {data.download_url.success && (
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() =>
                window.open(data.download_url?.success, "_blank")
              }
            >
              Success CSV
            </button>
          )}

          {data.download_url.failed && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() =>
                window.open(data.download_url?.failed, "_blank")
              }
            >
              Failed CSV
            </button>
          )}

        </div>
      );

    default:
      return "-";
  }

}}
          />

        </div>

      </div>

    </div>
  );
}